import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import SellerFlow from "./SellerFlow";
import SellerAccessGate, { maskEmail } from "./SellerAccessGate";
import GhostFibers from "./GhostFibers";
import { supabase } from "./supabaseClient"; // Adjust this path to match your project setup
import { TermsAcceptanceSheet } from "./LegalSheets";
import { hasAcceptedTerms, recordTermsAcceptance } from "./LegalContent";
import {
  ActiveSellerDashboard,
  getThemeFamilyForCategory,
} from "./ActiveSellerDashboard";
// Soft entrances for the screens that used to pop in after a black gap.
// The loading placeholder waits 150ms before showing, so a fast load never
// flashes it at all.
const SP_TRANSITION_CSS = `
  @keyframes sp-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes sp-rise-in {
    from { opacity: 0; transform: translateY(14px) scale(0.98); }
    to { opacity: 1; transform: none; }
  }
  .sp-screen-in { animation: sp-fade-in 0.6s ease-out both; }
  .sp-card-in { animation: sp-rise-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both; }
  .sp-loading-in { animation: sp-fade-in 0.4s ease-out 0.15s both; }
  @media (prefers-reduced-motion: reduce) {
    .sp-screen-in, .sp-card-in, .sp-loading-in { animation: none; }
  }
`;

export default function SellerPortal({
  navigateTo,
  currentScreen,
  setListings,
  onSellerReady,
  onRequestHomeTransition,
  runTileTransition,
  onRequestDashboardTransition,
}: {
  navigateTo: (screen: string) => void;
  currentScreen?: string;
  // Fired once the destination behind the App-level tile transition is
  // actually ready to reveal. For the approved/dashboard path this fires
  // once the dashboard's own data has finished loading, with the seller's
  // resolved category-theme hex so the tiles can flash to it right before
  // opening. Every other path (pending, rejected, no application, no
  // session) fires it with no argument as soon as that state is known —
  // there's no dashboard theme to swap to on those screens.
  onSellerReady?: (themeHex?: string) => void;
  // Passed straight through to ActiveSellerDashboard's Home button, so
  // clicking Home triggers the reverse tile transition (closing in the
  // seller's own category color) instead of navigating instantly.
  onRequestHomeTransition?: (themeHex: string) => void;
  // App-level generic tile-wipe runner (same one EntryScreen and every
  // other "back to entry" button already uses): sweeps the tiles in, runs
  // the given action hidden underneath, then retracts them. Used here by
  // the pending/rejected screens' Back button so leaving them gets the
  // same tile transition as the rest of the app, instead of an instant
  // cut. Falls back to a plain navigateTo if it's ever not supplied.
  runTileTransition?: (
    action: () => void | Promise<void>,
    color?: string
  ) => void;
  // Forwarded straight through to SellerFlow below (see its own use of
  // this prop) so a listing created/edited there can update App's
  // top-level `listings` state immediately, without waiting on a refetch.
  setListings?: React.Dispatch<React.SetStateAction<any[]>>;
  // Runs the tile transition that follows a correct verification code: tiles
  // close over the code screen, `reveal` is called underneath them (it mounts
  // the dashboard), the tiles wait while the dashboard loads and flash to the
  // seller's category color, then open onto the dashboard. If it isn't
  // supplied the dashboard just appears.
  onRequestDashboardTransition?: (reveal: () => void) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [hasUser, setHasUser] = useState<boolean>(false);
  const [showSellerLogin, setShowSellerLogin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  // Approved sellers must enter the emailed verification code once before
  // the dashboard opens. `accessVerified` mirrors seller_applications.
  // access_verified_at (true = they've already been through the code screen).
  const [accessVerified, setAccessVerified] = useState<boolean>(false);
  const [applicantEmail, setApplicantEmail] = useState<string | null>(null);
  // Resolved here, during the "One moment..." loading screen, from the
  // seller's own category — and handed to ActiveSellerDashboard as its
  // starting theme so the dashboard's very first render already uses the
  // correct color. This is what stops the brief flash of the default
  // orange theme that used to show before the dashboard's own fetch
  // finished resolving it.
  const [themeFamily, setThemeFamily] = useState<string>("orange");

  // --- "REVIEW OUR TERMS" (approved sellers) ---
  // An approved seller sees the Terms of Use / Privacy Policy sheet once (and
  // again whenever TERMS_VERSION changes) as they land on their dashboard —
  // whether they entered it normally or were just approved while sitting on the
  // pending screen. It waits for the dashboard's own first load, plus a beat
  // for the tile reveal to finish, so it never opens underneath the cover.
  const [dashboardReady, setDashboardReady] = useState(false);
  const [showSellerTerms, setShowSellerTerms] = useState(false);
  useEffect(() => {
    if (status !== "approved" || !userId || !dashboardReady) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    hasAcceptedTerms("seller", userId).then((accepted) => {
      if (cancelled || accepted) return;
      timer = setTimeout(() => setShowSellerTerms(true), 1200);
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [status, userId, dashboardReady]);

  // Shared by the pending/rejected screens' Back button: wraps the plain
  // navigateTo("entry") in the app's generic tile-wipe transition (tiles
  // sweep in, hide the navigate, then retract) instead of an instant cut.
  // Falls back to navigating directly if runTileTransition wasn't passed
  // down for some reason.
  const handleBackToEntry = () => {
    if (runTileTransition) {
      runTileTransition(() => navigateTo("entry"), "#ffb800");
    } else {
      navigateTo("entry");
    }
  };

  const handleGoogleStatusSync = async () => {
    try {
      // 1. Fetch current logged-in user profile from Google session
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError || !user) {
        // Instead of alerting, trigger the actual Google sign-in flow!
        await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: window.location.origin,
          },
        });
        return;
      }

      // 2. Scan the database row for this signed-in user (the application id is the auth user id)
      const { data: application, error: dbError } = await supabase
        .from("seller_applications")
        .select("status_type")
        .eq("id", user.id)
        .maybeSingle();

      if (dbError) throw dbError;

      if (!application) {
        alert(
          "No workspace registration found for this email. Please complete the application form first!"
        );
        return;
      }

      // 3. Update status to trigger structural re-routing gates
      if (application.status_type === "approved") {
        setStatus("approved");
        setShowSellerLogin(false); // Reset view flag
      } else if (application.status_type === "pending") {
        setStatus("pending");
        setShowSellerLogin(false);
      } else if (application.status_type === "rejected") {
        setStatus("rejected");
        setShowSellerLogin(false);
      } else {
        alert(`Application status is currently: ${application.status_type}`);
      }
    } catch (err) {
      console.error("Error looking up application identity:", err);
      alert("An error occurred while syncing your status. Please try again.");
    }
  };
  useEffect(() => {
    async function checkSeller() {
      try {
        // 1. Get the logged-in user session
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          // Signed-out visitor — there's no workspace to load, so skip
          // straight past the loading screen into the registration form.
          setHasUser(false);
          setLoading(false);
          onSellerReady?.();
          return;
        }

        setHasUser(true);
        setUserId(user.id);

        // 2. Fetch their application status by id — SF6 always inserts the
        // row with id = the auth user's own id, so this is a direct,
        // reliable match. (Previously this also OR'd in an email.ilike
        // match, but building that as a raw template-literal string broke
        // for emails containing special characters like "+", causing
        // PostgREST to return an error for the whole query — which we'd
        // silently swallow and fall back to showing the registration form
        // again, even for sellers who'd already applied.)
        const { data, error } = await supabase
          .from("seller_applications")
          .select("status_type, category, access_verified_at")
          .eq("id", user.id)
          .maybeSingle();

        if (error) {
          // Fail CLOSED: if the status can't be read, never guess. We used to
          // retry without the verification column and treat the seller as
          // already verified, which let anyone past the code screen whenever
          // that query failed. Now the seller sees a "couldn't load" screen
          // with a Try again button instead.
          console.error("Error fetching status:", error.message);
          setLoadError(true);
          setLoading(false);
          onSellerReady?.();
          return;
        }

        // 3. Save the status ('pending', 'approved', or null if no record exists)
        if (data) {
          // Set the status first, immediately — this is what actually
          // drives routing (pending/approved/rejected screens). Theme
          // resolution below is cosmetic only and must never be allowed to
          // block it: if getThemeFamilyForCategory throws for any reason,
          // we still want the seller correctly routed, just with the
          // default theme color instead of a crash back to the form.
          setAccessVerified(!!data.access_verified_at);
          setApplicantEmail(user.email ?? null);
          setStatus(data.status_type);

          try {
            // Resolve the dashboard's accent theme now, purely so the
            // dashboard itself renders correctly-themed from its very
            // first paint — NOT used to time the tile color swap (that
            // waits for the dashboard's own onInitialLoadComplete instead,
            // so tiles only flash to the real color right before opening).
            setThemeFamily(getThemeFamilyForCategory(data.category));
          } catch (themeErr) {
            console.error("Failed to resolve dashboard theme:", themeErr);
            // Falls back to the default "orange" themeFamily state — not
            // ideal, but never worth blocking the actual status routing.
          }

          if (data.status_type === "approved") {
            // Not through the code screen yet: there's no dashboard to wait
            // on, so let the tiles open straight onto the code screen.
            if (!data.access_verified_at) {
              setLoading(false);
              onSellerReady?.();
              return;
            }
            // Mount the dashboard right away — it starts fetching
            // immediately, hidden behind the App-level tile transition.
            // onSellerReady fires later, from ActiveSellerDashboard's own
            // onInitialLoadComplete, once its data has actually finished
            // loading (with the resolved theme hex in hand).
            setLoading(false);
            return;
          }

          // Pending/rejected have no heavy dashboard fetch behind them —
          // the tile cover animation itself already provides the pacing,
          // so reveal as soon as we know the status.
        } else {
          // Brand-new seller with no application on file — nothing to load,
          // so skip the loading screen entirely and go straight to the form.
          setStatus(null);
          navigateTo("seller-flow");
        }
        setLoading(false);
        onSellerReady?.();
      } catch (err) {
        console.error("Unexpected error:", err);
        setLoadError(true);
        setLoading(false);
        onSellerReady?.();
      }
    }

    // Runs once per mount (and again when the seller taps "Try again").
    // App keeps a single SellerPortal alive while the screen switches between
    // "seller-portal" and "seller-flow", so no second copy re-checks status.
    checkSeller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  // While the seller is on the pending-approval screen, listen live for their
  // seller_applications row flipping to "approved" — no reload needed. Note:
  // this live path isn't wrapped in the App-level tile transition (that
  // only wraps the initial Enter click / Home button), so it just reveals
  // the dashboard directly once its own data finishes loading.
  useEffect(() => {
    if (!userId || status !== "pending") return;

    const channel = supabase
      .channel(`seller-application-status-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "seller_applications",
          filter: `id=eq.${userId}`,
        },
        (payload: any) => {
          if (payload.new?.status_type === "approved") {
            setAccessVerified(!!payload.new?.access_verified_at);
            if (payload.new?.email) setApplicantEmail(payload.new.email);
            setStatus("approved");
          } else if (payload.new?.status_type) {
            setStatus(payload.new.status_type);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, status]);

  if (loading) {
    // When the App-level tile transition is covering the screen this is never
    // seen. When there is no tile cover (e.g. "Go to Seller Portal" from the
    // success screen) this replaces what used to be a bare black screen with
    // a calm placeholder that fades in, then gives way to the real screen.
    return (
      <div className="flex h-screen w-[31rem] max-w-full items-center justify-center bg-[#0D0917]">
        <style>{SP_TRANSITION_CSS}</style>
        <div className="sp-loading-in flex flex-col items-center gap-3 text-white">
          <svg
            className="h-8 w-8 animate-spin text-[#FFB800]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-sm font-medium tracking-wide text-white">
            One moment…
          </span>
        </div>
      </div>
    );
  }

  // Couldn't read the seller's status. Never fall through to the form or the
  // dashboard on a guess — let them retry or leave.
  if (loadError) {
    return (
      <div className="sp-screen-in flex h-screen w-[31rem] max-w-full flex-col items-center justify-center bg-[#0D0917] p-6 text-center">
        <style>{SP_TRANSITION_CSS}</style>
        <h2 className="mb-2 text-xl font-bold text-white">
          We couldn&apos;t load your seller account
        </h2>
        <p className="mb-6 max-w-xs text-sm leading-relaxed text-white/70">
          Please check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => {
            setLoadError(false);
            setLoading(true);
            setRetryKey((k) => k + 1);
          }}
          className="w-full max-w-xs rounded-xl bg-[#FFB800] py-3.5 font-bold text-black active:scale-95 transition-transform"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => navigateTo("entry")}
          className="mt-4 text-sm font-semibold text-white/70"
        >
          Back
        </button>
      </div>
    );
  }

  // CASE 1: The seller application is still pending
  if (status === "pending") {
    return (
      <div className="sp-screen-in relative flex flex-col justify-center items-center h-screen p-6 text-center overflow-hidden bg-[#0D0917]">
        <style>{SP_TRANSITION_CSS}</style>
        {/* Animated backdrop — sits behind everything else on this screen. */}
        <div className="absolute inset-0 z-0">
          <GhostFibers
            lineColor="#1006c5"
            glowColor="#ffb800"
            speed={0.2}
            scale={2}
            rotation={0}
            rotationSpeed={0.25}
            layers={4}
            waveAmplitude={0.015}
            waveFrequency={3}
            waveSpeed={0.15}
            layerSpeed={0.08}
            twist={0.1}
            twistFrequency={5}
            twistSpeed={1.2}
            lineFrequency={5}
            lineSpacing={2}
            lineSharpness={16}
            glowFalloff={10}
            glowIntensity={1.6}
            brightness={2}
            blueBoost={1.25}
            vignette={0.8}
            grain={0.05}
            dpr={1}
            lightMode={false}
            fps={60}
            paused={false}
          />
        </div>

        <button
          onClick={handleBackToEntry}
          className="absolute top-6 left-6 z-10 flex items-center gap-1.5 text-sm font-semibold text-white transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>

        <div className="sp-card-in relative z-10 bg-black/40 backdrop-blur-xl p-8 rounded-xl shadow-2xl shadow-black/50 max-w-md border border-white/10">
          <div className="flex justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-12 h-12 text-white animate-pulse"
            >
              <path d="M5 2h14" />
              <path d="M5 22h14" />
              <path d="M19 2v4c0 3.3-2.7 6-6 6s-6-2.7-6-6V2Z" />
              <path d="M7 22v-4c0-3.3 2.7-6 6-6s6 2.7 6 6v4Z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Application Under Review
          </h2>
          <p className="text-white mb-4">
            Thank you for applying to INUA! Our team is currently reviewing your
            details.
          </p>
          <p className="text-sm text-white/80 mb-6">
            Once your account is verified, we'll email you a welcome message
            together with your verification code. You'll need that code to enter
            your seller dashboard.
          </p>
          <p className="text-sm text-white bg-[#00E58A]/25 border border-[#00E58A]/50 py-2 px-4 rounded-lg inline-block">
            Status: Pending Approval
          </p>
        </div>
      </div>
    );
  }

  // CASE 1.5: The seller application was reviewed and rejected
  if (status === "rejected") {
    return (
      <div className="sp-screen-in relative flex flex-col justify-center items-center h-screen p-6 text-center overflow-hidden bg-[#0D0917]">
        <style>{SP_TRANSITION_CSS}</style>
        {/* Animated backdrop — sits behind everything else on this screen. */}
        <div className="absolute inset-0 z-0">
          <GhostFibers
            lineColor="#1006c5"
            glowColor="#ffb800"
            speed={0.2}
            scale={2}
            rotation={0}
            rotationSpeed={0.25}
            layers={4}
            waveAmplitude={0.015}
            waveFrequency={3}
            waveSpeed={0.15}
            layerSpeed={0.08}
            twist={0.1}
            twistFrequency={5}
            twistSpeed={1.2}
            lineFrequency={5}
            lineSpacing={2}
            lineSharpness={16}
            glowFalloff={10}
            glowIntensity={1.6}
            brightness={2}
            blueBoost={1.25}
            vignette={0.8}
            grain={0.05}
            dpr={1}
            lightMode={false}
            fps={60}
            paused={false}
          />
        </div>

        <button
          onClick={handleBackToEntry}
          className="absolute top-6 left-6 z-10 flex items-center gap-1.5 text-sm font-semibold text-white transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>

        <div className="sp-card-in relative z-10 bg-black/40 backdrop-blur-xl p-8 rounded-xl shadow-2xl shadow-black/50 max-w-md border border-white/10">
          <div className="flex justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-12 h-12 text-[#FF3B5C]"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m15 9-6 6" />
              <path d="m9 9 6 6" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Application Not Approved
          </h2>
          <p className="text-white mb-6">
            After review, we weren't able to approve your seller application.
            We'll email you the details of what needs fixing. You're welcome to
            apply again after 24 hours.
          </p>
          <p className="text-sm text-white bg-[#FF3B5C]/25 border border-[#FF3B5C]/50 py-2 px-4 rounded-lg inline-block">
            Status: Rejected
          </p>
        </div>
      </div>
    );
  }

  // CASE 1.8: Approved, but the emailed verification code hasn't been entered yet.
  if (status === "approved" && !accessVerified) {
    return (
      <SellerAccessGate
        maskedEmail={applicantEmail ? maskEmail(applicantEmail) : null}
        onBack={handleBackToEntry}
        onVerified={() => {
          const reveal = () => setAccessVerified(true);
          if (onRequestDashboardTransition) onRequestDashboardTransition(reveal);
          else reveal();
        }}
      />
    );
  }

  // CASE 2: The seller application has been approved!
  if (status === "approved") {
    return (
      <>
        <ActiveSellerDashboard
          navigateTo={navigateTo}
          initialThemeFamily={themeFamily}
          onRequestHomeTransition={onRequestHomeTransition}
          onInitialLoadComplete={(hex?: string) => {
            onSellerReady?.(hex);
            setDashboardReady(true);
          }}
        />
        {/* Rendered into the app's themed root (not inline) so it can't be
            affected by any transformed ancestor, and still gets the theme
            variables. */}
        {createPortal(
          <TermsAcceptanceSheet
            isOpen={showSellerTerms}
            onAgree={() => {
              if (userId) void recordTermsAcceptance("seller", userId);
              setShowSellerTerms(false);
            }}
            onCancel={() => {
              // Declining leaves the dashboard: back to the Buyer/Seller
              // screen, with the same tile wipe the other Back buttons use.
              setShowSellerTerms(false);
              if (runTileTransition) {
                runTileTransition(() => navigateTo("entry"), "#ffb800");
              } else {
                navigateTo("entry");
              }
            }}
          />,
          (document.querySelector("[data-theme]") as HTMLElement) ||
            document.body
        )}
      </>
    );
  }
  // CASE 2.5: User clicked "Log in here" from the application form
  if (showSellerLogin) {
    return (
      <div className="min-h-screen bg-[#0B0F19] text-white flex items-center justify-center p-6 w-full">
        <div className="bg-[#111827] border border-gray-800 p-8 rounded-2xl max-w-md w-full shadow-2xl">
          <h3 className="text-2xl font-bold mb-6 text-center text-white">
            Seller Workspace Access
          </h3>

          {/* We will handle the submit function in Step 4 */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              alert("Credentials login coming in next step!");
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                Email Address
              </label>
              <input
                type="email"
                required
                className="w-full bg-[#1F2937] border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                Password
              </label>
              <input
                type="password"
                required
                className="w-full bg-[#1F2937] border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35]"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#FF6B35] hover:bg-[#E05320] text-white font-bold py-3 rounded-xl transition shadow-lg mt-2"
            >
              Verify & Enter Workspace
            </button>
          </form>

          <div className="relative flex py-4 items-center">
            <div className="flex-grow border-t border-gray-800"></div>
            <span className="flex-shrink mx-4 text-gray-500 text-xs font-bold tracking-widest uppercase">
              OR
            </span>
            <div className="flex-grow border-t border-gray-800"></div>
          </div>

          {/* Dynamic Lookup Trigger via Google */}
          <button
            onClick={handleGoogleStatusSync}
            className="w-full flex items-center justify-center gap-3 bg-[#1F2937] border border-gray-700 hover:bg-[#253246] text-white font-medium py-3 px-4 rounded-xl transition duration-200 shadow-md"
          >
            <img
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google"
              className="w-5 h-5"
            />
            Continue with Google
          </button>

          <button
            type="button"
            onClick={() => setShowSellerLogin(false)}
            className="w-full text-center text-xs text-gray-500 hover:text-gray-400 underline mt-6 block bg-transparent border-none cursor-pointer"
          >
            Back to registration form
          </button>
        </div>
      </div>
    );
  }
  // CASE 2.8: Render Registration Form if not logging in
  if (!showSellerLogin) {
    return (
      <SellerFlow
        navigateTo={navigateTo}
        showSellerLogin={showSellerLogin}
        setShowSellerLogin={setShowSellerLogin}
        setListings={setListings}
      />
    );
  }
  // CASE 3: No user session found (e.g., they aren't logged in at all)
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white flex items-center justify-center p-6 w-full">
      <div className="bg-[#111827] border border-gray-800 p-8 rounded-2xl max-w-md w-full text-center shadow-xl">
        <h2 className="text-3xl font-extrabold text-white mb-2 tracking-wide">
          JOIN <span className="text-[#A855F7]">INUA</span>
        </h2>
        <p className="text-gray-400 text-sm mb-8">
          Create a seller account to set up your workspace
        </p>

        <button
          onClick={async () => {
            try {
              const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: window.location.origin,
                },
              });
              if (error) throw error;
            } catch (err: any) {
              alert(`Google Sign-In failed: ${err.message}`);
            }
          }}
          className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 font-semibold py-3 px-4 rounded-xl transition duration-200 hover:bg-gray-100 mb-6 shadow-md"
        >
          <img
            src="https://www.svgrepo.com/show/475656/google-color.svg"
            alt="Google"
            className="w-5 h-5"
          />
          Continue with Google
        </button>

        <button
          onClick={() => navigateTo("entry")}
          className="text-sm text-gray-500 hover:text-gray-400 underline decoration-dotted transition block mx-auto"
        >
          Cancel and go back
        </button>
      </div>
    </div>
  );
}
